import { getCsrfToken } from "~/lib/utils"
import {
  follow_up_step_campaign_path,
  follow_ups_campaign_path,
  spam_score_campaign_path,
  duplicate_follow_up_campaign_path,
  campaign_send_test_path,
} from "~/lib/routes"
import type { FollowUp } from "./types"

interface UpdateFollowUpParams {
  campaignId: number
  step: number
  subject?: string
  body?: string
  wait_days?: number
  message_format?: "html" | "text"
}

interface ApiResponse {
  success: boolean
  error?: string
}

export async function updateFollowUp(params: UpdateFollowUpParams): Promise<ApiResponse> {
  const { campaignId, step, ...followUpParams } = params

  const response = await fetch(follow_up_step_campaign_path(campaignId, step), {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": getCsrfToken(),
    },
    body: JSON.stringify({ follow_up: followUpParams }),
  })

  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    return { success: false, error: data.error || "Failed to update follow-up" }
  }

  return { success: true }
}

interface CreateFollowUpParams {
  campaignId: number
  step: number
}

interface CreateFollowUpResponse {
  success: boolean
  error?: string
  followUp?: FollowUp
}

export async function createFollowUp(params: CreateFollowUpParams): Promise<CreateFollowUpResponse> {
  const { campaignId, step } = params

  const response = await fetch(follow_up_step_campaign_path(campaignId, step), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": getCsrfToken(),
    },
    body: JSON.stringify({
      follow_up: {
        subject: "",
        body: "",
        wait_days: 3,
      },
    }),
  })

  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    return { success: false, error: data.error || "Failed to create follow-up" }
  }

  const followUp = await response.json()
  return { success: true, followUp }
}

interface DeleteFollowUpParams {
  campaignId: number
  step: number
}

export async function deleteFollowUp(params: DeleteFollowUpParams): Promise<ApiResponse> {
  const { campaignId, step } = params

  const response = await fetch(follow_up_step_campaign_path(campaignId, step), {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": getCsrfToken(),
    },
  })

  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    return { success: false, error: data.error || "Failed to delete follow-up" }
  }

  return { success: true }
}

export async function fetchFollowUps(campaignId: number): Promise<FollowUp[]> {
  const response = await fetch(follow_ups_campaign_path(campaignId, { format: "json" }), {
    method: "GET",
    headers: {
      Accept: "application/json",
    },
  })

  if (!response.ok) {
    throw new Error("Failed to fetch follow-ups")
  }

  return response.json()
}

export type SpamScoreReason = "email_is_too_long" | "rate_limited" | "internal_error"

export interface SpamScoreResult {
  result: number | "unknown"
  reason?: string | SpamScoreReason
}

interface CheckSpamScoreParams {
  campaignId: number
  step: number
}

export async function checkSpamScore(params: CheckSpamScoreParams): Promise<SpamScoreResult> {
  const { campaignId, step } = params

  const response = await fetch(spam_score_campaign_path(campaignId, step, { format: "json" }), {
    method: "GET",
    headers: {
      Accept: "application/json",
    },
  })

  if (!response.ok) {
    return { result: "unknown", reason: "internal_error" }
  }

  return response.json()
}

interface DuplicateFollowUpParams {
  campaignId: number
  step: number
}

interface DuplicateFollowUpResponse {
  success: boolean
  error?: string
  followUp?: FollowUp
}

export async function duplicateFollowUp(params: DuplicateFollowUpParams): Promise<DuplicateFollowUpResponse> {
  const { campaignId, step } = params

  const response = await fetch(duplicate_follow_up_campaign_path(campaignId, step), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": getCsrfToken(),
    },
  })

  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    return { success: false, error: data.error || "Failed to duplicate follow-up" }
  }

  const followUp = await response.json()
  return { success: true, followUp }
}

interface SendTestEmailParams {
  campaignId: number
  step: number
  email: string
}

interface SendTestEmailResponse {
  success: boolean
  message: string
}

export async function sendTestEmail(params: SendTestEmailParams): Promise<SendTestEmailResponse> {
  const { campaignId, step, email } = params

  const response = await fetch(campaign_send_test_path(campaignId), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": getCsrfToken(),
    },
    body: JSON.stringify({ step, email }),
  })

  const data = await response.json()

  if (!response.ok) {
    return { success: false, message: data.message || "Failed to send test email" }
  }

  return { success: true, message: data.message }
}
